// THE ONE SCREEN FOR ALL THE MONEY (/money).
//
// It answers, before anything else, what an owner asks of money: what are we owed, what
// is late, what is waiting on us. Then one of two views of the same rows:
//
//   מה צריך לעשות   (default) three columns — late, ours to do, not yet due — each row
//                   naming what it belongs to (client › retainer › job)
//   לפי לקוח        client › retainer › job, every charge under its own job
//
// Paid clients are hidden by default behind a switch at the top that names how many.
//
// ONE READ, AND THE WRITES LIVE IN THEIR OWN DOMAINS. One fetch (collectibles.compute);
// every act opens its dialog (BoardDialogs), and recording an invoice or a payment goes to
// the invoices / payments endpoints as it always has. No figure is computed in this
// folder: the headline and the late figure are the server's totals, the rows are the
// server's rows, and the views only group and order them. `npm test` pins that nothing
// the server sent is dropped, and that a figure the server did not send reads "—".
import { useMemo, useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { collectionsApi } from '../../api';
import { useAlerts } from '../../context/AlertsContext';
import he from '../../i18n/he';
import AsyncBoundary from '../../components/Loader';
import ViewToggle from '../../components/ViewToggle';
import BoardDialogs from './BoardDialogs';
import { buildActions } from './actionRows';
import { buildClientTree } from './clientTree';
import Headline from './Headline';
import ActionList from './ActionList';
import ClientView from './ClientView';

const mp = he.moneyScreen;

export default function Collections() {
  const live = useAsync(() => collectionsApi.list());
  const [byClient, setByClient] = useState(false);
  const [showSettled, setShowSettled] = useState(false);

  const { data } = live;
  const actions = useMemo(() => (data ? buildActions(data) : null), [data]);
  const tree = useMemo(() => (data ? buildClientTree(data) : []), [data]);

  // The dialogs — the current screen's own, driven by the same state shapes.
  const [recording, setRecording] = useState(null);
  const [paying, setPaying] = useState(null);
  const [skipping, setSkipping] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [paymentsOf, setPaymentsOf] = useState(null);
  const closeAll = () => {
    setRecording(null); setPaying(null); setSkipping(null); setCorrecting(null);
    setCancelling(null); setPaymentsOf(null);
  };
  // Every act here can light or clear the money dot, so the sidebar re-reads with the page.
  const alerts = useAlerts();
  const done = () => {
    closeAll(); live.reload();
    if (alerts) alerts.reload();
  };

  const liveActs = {
    onPay: (card, row) => setPaying(row),
    onCorrect: (card, row) => setCorrecting({ card, row }),
    onCancel: (card, row) => setCancelling({ card, row }),
    onPayments: (card, row) => setPaymentsOf({ card, row }),
    onRecordMonth: (card, month) => setRecording({ card, month }),
    onRecordJob: (card) => setRecording({ card }),
    onRaise: (card, row) => setRecording({ card, expense: row }),
    onSkip: (card, month) => setSkipping({ card, month }),
    // Restores the month to "to bill", which is the state the screen already shows.
    onUnskip: async (card, month) => { await collectionsApi.unskipMonth(card.id, month); live.reload(); },
    onFilesChanged: live.reload,
  };
  const acts = liveActs;

  const settledCount = tree.filter((c) => c.settled).length;

  return (
    <div className="mp">
      <div className="mp-top">
        <h1>{mp.title}</h1>
        <div className="mp-controls">
          <ViewToggle value={byClient} onChange={setByClient} offLabel={mp.viewTodo} onLabel={mp.viewByClient} />
          {/* Paid clients are hidden by default; the switch that shows them sits HERE, at the
              top, in both views, and names how many it hides. Paid items live in the by-client
              view (nothing paid is a thing to do), so turning it on opens that view. */}
          {settledCount > 0 && (
            <label className="mp-switch">
              <input type="checkbox" role="switch" checked={byClient && showSettled}
                onChange={(e) => { setShowSettled(e.target.checked); if (e.target.checked) setByClient(true); }} />
              <span className="mp-switch-track" aria-hidden />
              {mp.showSettled(settledCount)}
            </label>
          )}
        </div>
      </div>

      <AsyncBoundary loading={live.loading} error={live.error}>
        {data && (
          <div className={live.refreshing ? 'refreshing' : ''}>
            <Headline totals={data.totals} />
            {byClient
              ? <ClientView tree={tree} acts={acts} showSettled={showSettled} />
              : <ActionList actions={actions} totals={data.totals} acts={acts} />}
          </div>
        )}
      </AsyncBoundary>

      <BoardDialogs recording={recording} paying={paying} skipping={skipping}
          correcting={correcting} cancelling={cancelling} paymentsOf={paymentsOf}
          onClose={closeAll} onSaved={done} />
    </div>
  );
}
